import cn from 'classnames';
import { type FC, useMemo } from 'react';

import ReviewTable from '@/components/Studies/Create/Steps/Groups/ReviewTable';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import { _isEmpty, _isNotEmpty, _notNil } from '@/littledash';
import type { Study } from '@/model/Study.model';
import type { ClassificationType, TreatmentApiId } from '@/model/Treatment.model';
import type { GroupApiId, TreatmentGroup } from '@/model/TreatmentGroup.model';

import type { OverviewGroup, SuccessfulOverviewSection } from '../../OverviewAccordion.model';
import { toTreatmentGroup } from '../../OverviewAccordion.utils';
import { useOverviewAccordion } from '../../OverviewAccordionContext';
import overviewTableStyles from '../OverviewSectionTable.module.scss';
import { useOverviewTreatmentInfoModal } from '../useOverviewTreatmentInfoModal';

interface GroupsSectionContentProps {
  section: SuccessfulOverviewSection<'groups'>;
  study: Study;
}

type TreatmentIdField = 'treatment_api_ids' | 'pre_treatment_api_ids' | 'disease_api_ids';

const TREATMENT_COLUMNS: ReadonlyArray<{ field: TreatmentIdField; header: string }> = [
  { field: 'treatment_api_ids', header: 'Treatments' },
  { field: 'pre_treatment_api_ids', header: 'Pre-treatments' },
  { field: 'disease_api_ids', header: 'Diseases' },
];

const TreatmentLinksCell: FC<{
  treatmentApiIds: Array<TreatmentApiId> | undefined;
  onTreatmentClick: (apiId: TreatmentApiId, classification: ClassificationType) => void;
}> = ({ treatmentApiIds, onTreatmentClick }) => {
  const { treatmentsByApiId } = useOverviewAccordion();
  const treatments = (treatmentApiIds ?? []).reduce<
    Array<{ apiId: TreatmentApiId; label: string; classification: ClassificationType }>
  >((acc, apiId) => {
    const reference = treatmentsByApiId.get(apiId);
    if (_notNil(reference)) {
      acc.push({ apiId, label: reference.row.label, classification: reference.row.classification });
    }
    return acc;
  }, []);

  if (_isEmpty(treatments)) {
    return <>-</>;
  }

  return (
    <div className="flex flex-column items-start">
      {treatments.map(({ apiId, label, classification }) => (
        <button
          key={apiId}
          type="button"
          className="link-button link blue tl"
          data-testid={`overview-group-treatment-${apiId}`}
          onClick={() => onTreatmentClick(apiId, classification)}
        >
          {label}
        </button>
      ))}
    </div>
  );
};

const treatmentColumns = (
  rows: Array<OverviewGroup>,
  rowsByApiId: Map<GroupApiId | undefined, OverviewGroup>,
  onTreatmentClick: (apiId: TreatmentApiId, classification: ClassificationType) => void
): Array<TanStackColumnDef<TreatmentGroup>> =>
  TREATMENT_COLUMNS.reduce<Array<TanStackColumnDef<TreatmentGroup>>>((columns, { field, header }) => {
    if (rows.some((row) => _isNotEmpty(row[field]))) {
      columns.push({
        id: field,
        header,
        enableSorting: false,
        meta: { verticalAlign: 'start' },
        cell: ({ row: { original } }: { row: { original: TreatmentGroup } }) => (
          <TreatmentLinksCell
            treatmentApiIds={rowsByApiId.get(original.api_id)?.[field]}
            onTreatmentClick={onTreatmentClick}
          />
        ),
      });
    }
    return columns;
  }, []);

/**
 * Renders Overview groups with the same ReviewTable used on the study Groups tab.
 * Treatment, pre-treatment and disease links open the groups-table treatment info modal.
 */
const GroupsSectionContent: FC<GroupsSectionContentProps> = ({ section, study }) => {
  const groups = useMemo(() => section.rows.map(toTreatmentGroup), [section.rows]);
  const { loadingTreatment, openTreatmentInfo } = useOverviewTreatmentInfoModal(study.api_id);
  const rowsByApiId = useMemo(() => new Map(section.rows.map((row) => [row.api_id, row] as const)), [section.rows]);
  const extraColumns = treatmentColumns(section.rows, rowsByApiId, openTreatmentInfo);

  return (
    <div
      className={cn({ ui__disabled: loadingTreatment })}
      data-test-component="GroupsSectionContent"
      data-test-element="container"
    >
      <ReviewTable groups={groups} extraColumns={extraColumns} maxHeightClassName={overviewTableStyles.maxHeight} />
    </div>
  );
};

export default GroupsSectionContent;
